import { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useHistory } from 'react-router';
import { updateCacheById } from 'graphql/apollo/cache';
import { INITIAL_QUERY_STATE_CONFIG } from 'graphql/apollo/config';
import { useLoading } from 'ui/contexts/overlay/Loading';
import useToastContext from 'ui/hooks/useToast';
import { useConfirm } from 'ui/contexts/overlay/Confirm';
import { Dashboard, DashboardMainHeaderForm } from 'dashboard/components/dashboard';
import { Pagination } from 'dashboard/components/table';
import TariffGroupTable from 'tariffGroups/components/table/TariffGroupTable';
import { TARIFF_GROUPS_LIST_QUERY } from 'tariffGroups/graphql/tariffGroupsListQuery';
import {
  TARIFF_GROUP_DELETE_MUTATION,
  TariffGroupDeleteMutationVariables,
  TariffGroupTypename,
} from 'tariffGroups/graphql/tariffGroupDeleteMutation';
import { TariffGroupProps } from 'tariffGroups/models/tariffGroup';
import { ToastProps } from 'ui/contexts/overlay/Toast';
import AddButton from 'dashboard/components/dashboard/AddButton';

type State = {
  tariffGroups: TariffGroupProps[];
  hasNextPage: boolean;
  hasPreviousPage: boolean;
};

const LIST_ERROR_TOAST: ToastProps = {
  title: 'Algo deu errado!',
  variant: 'danger',
  text: 'Não foi possível carregar a lista de tarifa de energia',
};

const DELETE_SUCCESS_TOAST: ToastProps = {
  text: 'Tarifa de energia deletado com sucesso',
  title: 'Sucesso',
  variant: 'primary',
};

const DETETE_ERROR_TOAST: ToastProps = {
  text: 'Houve um erro ao tentar deletar tarifa de energia',
  title: 'Algo deu errado!',
  variant: 'danger',
};

const DASHBOARD_TARIFF_GROUPS_CREATE_ROUTE =
  '/dashboard/taxes-fees/tariff-groups/create';

export default function TariffGroupsPage() {
  const { push } = useHistory();
  const { addToast } = useToastContext();
  const { LoadingOverlay, showLoading, closeLoading } = useLoading();
  const { ConfirmOverlay, closeConfirm, showConfirm } = useConfirm();
  const [currentTariffGroupId, setCurrentTariffGroupId] = useState<number>(0);
  const [state, setState] = useState<State>({
    tariffGroups: [],
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const [tariffGroupDeleteMutation, { loading: tariffGroupDeleteLoading }] =
    useMutation<TariffGroupDeleteMutationVariables>(TARIFF_GROUP_DELETE_MUTATION, {
      onError() {
        addToast(DETETE_ERROR_TOAST);
      },
      onCompleted() {
        addToast(DELETE_SUCCESS_TOAST);
      },
    });

  const [tariffGroupsList, { data, loading, error, refetch }] = useLazyQuery(
    TARIFF_GROUPS_LIST_QUERY,
    INITIAL_QUERY_STATE_CONFIG
  );

  useEffect(() => {
    tariffGroupsList();
  }, [tariffGroupsList]);

  useEffect(() => {
    if (error) {
      addToast(LIST_ERROR_TOAST);
    }
  }, [addToast, error]);

  const isLoading = loading || tariffGroupDeleteLoading;

  useEffect(() => {
    if (isLoading) {
      showLoading();
      return;
    }

    if (data) {
      setState({
        tariffGroups: data.powerDistributionUnitTariffGroups.entries,
        hasNextPage: !!data.powerDistributionUnitTariffGroups.afterCursor,
        hasPreviousPage: !!data.powerDistributionUnitTariffGroups.beforeCursor,
      });
    }

    closeLoading();
  }, [closeLoading, data, isLoading, showLoading]);

  const handleClickNext = () => {
    refetch &&
      refetch({
        after: data.powerDistributionUnitTariffGroups.afterCursor,
        before: null,
      });
  };

  const handleClickBefore = () => {
    refetch &&
      refetch({
        after: null,
        before: data.powerDistributionUnitTariffGroups.beforeCursor,
      });
  };

  const onClickEditButton = (id: number) =>
    push(`/dashboard/taxes-fees/tariff-groups/${id}/edit`);

  const onClickRemoveButton = (id: number) => {
    setCurrentTariffGroupId(id);
    showConfirm();
  };

  const onConfirmDelete = () => {
    closeConfirm();

    tariffGroupDeleteMutation({
      variables: { id: currentTariffGroupId },
      update(cache) {
        updateCacheById(cache, currentTariffGroupId, TariffGroupTypename);
      },
    });
  };

  const onCancelDelete = () => closeConfirm();

  const onClickAddButton = () => push(DASHBOARD_TARIFF_GROUPS_CREATE_ROUTE);

  return (
    <Dashboard
      dashboardMainHeaderTitle={
        <DashboardMainHeaderForm title="Tarifas de energia">
          <AddButton onClick={onClickAddButton} label="Tarifa de energia" />
        </DashboardMainHeaderForm>
      }
    >
      <div className="rounded-lg">
        <TariffGroupTable
          tariffGroups={state.tariffGroups}
          onClickEditButton={onClickEditButton}
          onClickRemoveButton={onClickRemoveButton}
        />
        <Pagination
          onNextClick={handleClickNext}
          onPreviousClick={handleClickBefore}
          disableNext={!state.hasNextPage}
          disableBefore={!state.hasPreviousPage}
        />
        <ConfirmOverlay
          title="Remoção do tarifa de energia!"
          text="Tem certeza que deseja deletar este tarifa de energia? Todos os dados serão permanentemente removidos do nosso sistema. Essa ação não poderá ser revertida."
          variant="danger"
          onConfirm={onConfirmDelete}
          onCancel={onCancelDelete}
        />
      </div>
      <LoadingOverlay />
    </Dashboard>
  );
}
